import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import '../CSS/Archives.css';

// Configure pdf.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/* ── SVG icon helpers ── */
const IconArchive = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>
);
const IconBook = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
);
const IconEye = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const IconCalendar = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
);
const IconDownload = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);
const IconChevronLeft = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
);
const IconChevronRight = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
);
const IconX = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const IconFileText = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
);
const IconSend = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
);
const IconClock = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);

/* ── Volume/Issue data ── */
const archivesData = [
    {
        volume: 1,
        year: '2026',
        issues: [
            {
                issue: 1,
                title: 'Inaugural Issue',
                date: 'January 2026',
                status: 'published',
                pdfPath: '/archives/Volume-1/Issue-1/vol1_issue1_2026.pdf',
            },
            {
                issue: 2,
                title: 'AI-Enabled Engineering Systems for Healthcare & Life Sciences',
                date: 'July 2026',
                status: 'coming-soon',
                pdfPath: null,
            },
        ],
    },
];

/* ── PDF Viewer Modal using pdfjs-dist ── */
const PdfViewerModal = ({ pdfUrl, title, onClose }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [loading, setLoading] = useState(true);
    const [pdfDoc, setPdfDoc] = useState(null);
    const canvasRef = useRef(null);

    // Load the PDF document
    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const loadPdf = async () => {
            try {
                const doc = await pdfjsLib.getDocument(pdfUrl).promise;
                if (!cancelled) {
                    setPdfDoc(doc);
                    setNumPages(doc.numPages);
                    setPageNum(1);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error loading PDF:', err);
                if (!cancelled) setLoading(false);
            }
        };
        loadPdf();
        return () => { cancelled = true; };
    }, [pdfUrl]);

    // Render a page to canvas
    useEffect(() => {
        if (!pdfDoc || !canvasRef.current) return;
        let cancelled = false;

        const renderPage = async () => {
            try {
                const page = await pdfDoc.getPage(pageNum);
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;

                await page.render({ canvasContext: context, viewport }).promise;
            } catch (err) {
                if (!cancelled) console.error('Error rendering page:', err);
            }
        };
        renderPage();
        return () => { cancelled = true; };
    }, [pdfDoc, pageNum]);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // Prevent body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <div className="archives-modal-overlay" onClick={onClose}>
            <div className="archives-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="archives-modal-header">
                    <span className="archives-modal-title">
                        <IconFileText /> {title}
                    </span>
                    <div className="archives-modal-controls">
                        <button className="archives-modal-nav-btn" disabled={pageNum <= 1} onClick={() => setPageNum((p) => p - 1)} title="Previous page">
                            <IconChevronLeft />
                        </button>
                        {numPages && <span className="archives-modal-page-info">Page {pageNum} / {numPages}</span>}
                        <button className="archives-modal-nav-btn" disabled={!numPages || pageNum >= numPages} onClick={() => setPageNum((p) => p + 1)} title="Next page">
                            <IconChevronRight />
                        </button>
                        <button className="archives-modal-close-btn" onClick={onClose} title="Close">
                            <IconX />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="archives-modal-body">
                    {loading ? (
                        <div className="archives-pdf-loading">
                            <div className="archives-pdf-spinner" />
                            <span>Loading PDF…</span>
                        </div>
                    ) : (
                        <div className="archives-pdf-canvas-wrapper">
                            <canvas ref={canvasRef} />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="archives-modal-footer">
                    <a
                        className="archives-modal-download-btn"
                        href={pdfUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <IconDownload /> Download PDF
                    </a>
                </div>
            </div>
        </div>
    );
};

/* ── Main Archives Page ── */
const Archives = () => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [activePdf, setActivePdf] = useState({ url: '', title: '' });

    const openViewer = useCallback((url, title) => {
        setActivePdf({ url, title });
        setViewerOpen(true);
    }, []);

    const closeViewer = useCallback(() => {
        setViewerOpen(false);
        setActivePdf({ url: '', title: '' });
    }, []);

    return (
        <div className="archives-page">
            {/* ── Hero ── */}
            <div className="archives-hero">
                <div className="archives-hero-badge">
                    <IconArchive />
                    Journal Archives
                </div>
                <h1>Published Volumes &amp; Issues</h1>
                <p className="archives-hero-subtitle">
                    Browse the complete collection of peer-reviewed research published in Innovative Trends in Multidisciplinary Engineering.
                </p>
            </div>

            {/* ── Content ── */}
            <div className="archives-content">
                {archivesData.map((vol) => (
                    <div className="archives-volume-card" key={vol.volume}>
                        {/* Volume header */}
                        <div className="archives-volume-header">
                            <div className="archives-volume-icon"><IconBook /></div>
                            <div className="archives-volume-title-group">
                                <h2 className="archives-volume-title">Volume {vol.volume}</h2>
                                <span className="archives-volume-year">{vol.year}</span>
                            </div>
                        </div>

                        {/* Issue grid */}
                        <div className="archives-issue-grid">
                            {vol.issues.map((iss) => {
                                const isPublished = iss.status === 'published';

                                return (
                                    <div
                                        className={`archives-issue-card ${!isPublished ? 'coming-soon-card' : ''}`}
                                        key={iss.issue}
                                        onClick={isPublished ? () => openViewer(iss.pdfPath, `Volume ${vol.volume}, Issue ${iss.issue} — ${iss.title}`) : undefined}
                                        role={isPublished ? 'button' : undefined}
                                        tabIndex={isPublished ? 0 : undefined}
                                        onKeyDown={isPublished ? (e) => { if (e.key === 'Enter') openViewer(iss.pdfPath, `Volume ${vol.volume}, Issue ${iss.issue} — ${iss.title}`); } : undefined}
                                    >
                                        <div className="archives-issue-card-header">
                                            <span className="archives-issue-number">Issue {iss.issue}</span>
                                            <span className={`archives-issue-status ${iss.status}`}>
                                                {isPublished ? '● Published' : '◷ Coming Soon'}
                                            </span>
                                        </div>

                                        <h3 className="archives-issue-title">{iss.title}</h3>
                                        <p className="archives-issue-meta">
                                            {isPublished ? <IconCalendar /> : <IconClock />}
                                            {iss.date}
                                        </p>

                                        <div className="archives-issue-action">
                                            {isPublished ? (
                                                <span className="archives-issue-btn view-btn">
                                                    <IconEye /> View Issue
                                                </span>
                                            ) : (
                                                <span className="archives-issue-btn disabled-btn">
                                                    Upcoming
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                {/* ── Call for Papers CTA ── */}
                <div className="archives-cta-section">
                    <div className="archives-cta-card">
                        <div className="archives-cta-text">
                            <h3>Call for Papers</h3>
                            <p>
                                Submit your research to the next issue of ITME. We welcome original manuscripts across all engineering disciplines.
                            </p>
                        </div>
                        <Link to="/form" className="archives-cta-btn">
                            <IconSend /> Submit Manuscript
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── PDF Viewer Modal ── */}
            {viewerOpen && (
                <PdfViewerModal
                    pdfUrl={activePdf.url}
                    title={activePdf.title}
                    onClose={closeViewer}
                />
            )}
        </div>
    );
};

export default Archives;
